// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import {
  type DateOnly,
  dateOnly,
  formatDate,
  isDateOnly,
  today,
  useEvolvePaginatedGet,
  useEvolveGet,
  workOrderStateNames,
  type WorkOrderPaginated,
} from '@evolvemep/api-ts';
import { Badge, Button, Card, Flex, Loader, Text, useMantineTheme } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { useDebouncedValue } from '@mantine/hooks';
import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from 'date-fns';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { FakeDragDropContext } from 'components/Mantine/FakeDragDropContext';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { getAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';
import { NewWorkOrderModal } from 'modules/Shop/WorkOrders/WorkOrdersPage/NewWorkOrderModal';
import { WorkOrderKanbanCard } from 'modules/Shop/WorkOrders/WorkOrdersPage/WorkOrderKanbanCard';

import { calendarStyles, highlightDayStyles } from './calendarStyles';
import { PendingStatusRetirementBanner } from './PendingStatusRetirementBanner';

export const WorkOrdersCalendarView = () => {
  const {
    projectIds,
    facilityIds,
    projects: selectedProjects,
  } = useGlobalFilters({
    projects: true,
    facilities: true,
  });
  const [selectedDate, setSelectedDate] = useState(today());
  const [selectedMonth, setSelectedMonth] = useState(selectedDate);
  const [workOrders, setWorkOrders] = useState<WorkOrderPaginated[] | null>(null);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);

  const [debouncedMonth] = useDebouncedValue(selectedMonth, 750);

  const { fetchPage: fetchWorkOrders } = useEvolvePaginatedGet('shop/workOrder');

  const theme = useMantineTheme();

  const { data: projects } = useEvolveGet('shop/project/projectsForActiveWorkOrders', { lazy: false });

  useEffect(() => {
    setLoadingWorkOrders(true);
    void getAllDataFromFetcher(fetchWorkOrders, {
      params: {
        facilityIds,
        projectIds,
        needBy: `start:${dateOnly(
          startOfWeek(startOfMonth(debouncedMonth)),
        )},end:${dateOnly(endOfWeek(endOfMonth(debouncedMonth)))}`,
      },
    })
      .then((res) => {
        setWorkOrders(
          res
            .toSorted(
              (a, b) =>
                workOrderStateNames.indexOf(a.workOrderStateName) - workOrderStateNames.indexOf(b.workOrderStateName),
            )
            .toSorted((a, b) => a.projectName.localeCompare(b.projectName)),
        );
      })
      .finally(() => setLoadingWorkOrders(false));
  }, [debouncedMonth, fetchWorkOrders, facilityIds, projectIds]);

  return (
    <>
      <BasePageHeader
        title="Work Orders"
        topLeftComponent={(loadingWorkOrders || selectedMonth !== debouncedMonth) && <Loader size="sm" />}
        topRightComponent={
          <NewWorkOrderModal defaultProject={hasLengthExactly(selectedProjects, 1) ? selectedProjects[0] : undefined} />
        }
      />

      <PendingStatusRetirementBanner />

      <Flex h="100%" style={{ overflow: 'hidden' }} gap="sm">
        <Card miw={350} h="100%" p={0} withBorder style={{ overflowY: 'auto' }}>
          <Flex justify="space-between" mb="lg" bg="gray.1" p="md">
            <Badge h={50} w={50} radius={25} bg={`${theme.primaryColor}.1`} c={theme.primaryColor} fz={18}>
              {formatDate(selectedDate, 'd')}
            </Badge>
            <div>
              <Text size="xl" fw={600}>
                {formatDate(selectedDate, 'cccc')}
              </Text>
              <Text>{formatDate(selectedDate, 'PPP')}</Text>
            </div>
            <Button
              variant="light"
              onClick={() => {
                setSelectedDate(today());
                setSelectedMonth(today());
              }}
            >
              Today
            </Button>
          </Flex>

          {/* KanbanCards use `Draggable`, so they *must* exist inside a DragDropContext*/}
          <FakeDragDropContext>
            <Flex direction="column" gap="xs" p="xs" w="100%">
              {workOrders
                ?.filter((wo) => wo.needByDate === selectedDate)
                .map((workOrder, index) => (
                  <WorkOrderKanbanCard
                    key={workOrder.workOrderId}
                    index={index}
                    workOrder={workOrder}
                    draggable={false}
                  />
                ))}
            </Flex>
          </FakeDragDropContext>
        </Card>
        <Calendar
          className="big-calendar"
          onDateChange={(m) => setSelectedMonth(m as DateOnly)}
          date={selectedMonth}
          w="100%"
          h="100%"
          styles={calendarStyles}
          renderDay={(date) => {
            if (!isDateOnly(date)) return null;
            const day = formatDate(date, 'dd');
            return (
              <Flex
                key={date}
                onClick={() => setSelectedDate(date)}
                direction="column"
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  width: '100%',
                  ...(selectedDate === date ? highlightDayStyles : {}),
                }}
              >
                <Text w="100%" bg="gray.1" ta="center" c="black" style={{ position: 'sticky', top: 0 }}>
                  {day === '01' ? `${formatDate(date, 'LLL')} 01` : day}
                </Text>
                <Flex mt={4} direction="column" style={{ flex: '1 1 auto', overflowY: 'auto', width: '100%' }}>
                  <Flex direction="column" gap={3} style={{ flex: '1 0 auto' }}>
                    {projects?.map((p) => {
                      const filtered = workOrders?.filter(
                        (wo) => wo.projectId === p.projectId && wo.needByDate === date,
                      );
                      if (isNotNil(filtered) && filtered.length > 0) {
                        return (
                          <Badge
                            key={p.projectId}
                            color={`${theme.primaryColor}.0`}
                            size="lg"
                            w="100%"
                            styles={{
                              root: {
                                justifyContent: 'space-between',
                              },
                              label: {
                                textAlign: 'left',
                                textTransform: 'capitalize',
                                color: 'black',
                                fontWeight: 'normal',
                              },
                            }}
                            radius={3}
                            rightSection={
                              <Badge
                                size="xs"
                                color="white"
                                c="dark"
                                style={{ backgroundColor: 'white', alignSelf: 'right' }}
                              >
                                {filtered.length}
                              </Badge>
                            }
                          >
                            {p.projectName}
                          </Badge>
                        );
                      }
                      return null;
                    })}
                  </Flex>
                </Flex>
              </Flex>
            );
          }}
        />
      </Flex>
    </>
  );
};
